import { notifications } from "@mantine/notifications"
import { AlertCircle, CheckCircle } from "lucide-react"
import { createElement } from "react"
import { create } from "zustand"

import { membersService } from "@/services/members.service"
import type { BulkActionPayload, Member, MemberFormData, MemberProfileFormData, PermissionType } from "@/types"

interface MembersState {
  // State
  members: Member[]
  isLoading: boolean
  error: string | null
  selectedMember: Member | null
  unassignedMembers: Member[]
  isLoadingUnassigned: boolean

  // Actions
  fetchMembers: () => Promise<void>
  fetchUnassignedMembers: () => Promise<void>
  getMemberById: (id: number) => Promise<void>
  createMember: (formData: MemberFormData) => Promise<Member | null>
  updateMember: (id: number, formData: Partial<MemberFormData>) => Promise<Member | null>
  /** Profile-only update (PATCH, first/last name); used by a department admin. */
  updateMemberProfile: (id: number, profile: MemberProfileFormData) => Promise<Member | null>
  activateMember: (id: number) => Promise<Member | null>
  deactivateMember: (id: number) => Promise<Member | null>
  updateMemberPermissions: (id: number, permissionTypes: PermissionType[]) => Promise<boolean>
  deleteMember: (id: number) => Promise<boolean>
  assignMemberToDepartment: (memberId: number, departmentId: number) => Promise<boolean>
  removeMemberFromDepartment: (memberId: number) => Promise<boolean>
  bulkAction: (payload: BulkActionPayload) => Promise<boolean>
  setSelectedMember: (member: Member | null) => void
  clearError: () => void
  reset: () => void
}

const initialState = {
  members: [],
  isLoading: false,
  error: null,
  selectedMember: null,
  unassignedMembers: [],
  isLoadingUnassigned: false,
}

export const useMembersStore = create<MembersState>((set, get) => ({
  ...initialState,

  /**
   * Fetch all members
   */
  fetchMembers: async () => {
    set({ isLoading: true, error: null })
    try {
      const members = await membersService.getAllMembers()
      set({ members, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch members"
      set({ error: errorMessage, isLoading: false })
      notifications.show({
        title: "Error Loading Members",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
    }
  },

  /**
   * Fetch the pool of members with no department assigned
   */
  fetchUnassignedMembers: async () => {
    set({ isLoadingUnassigned: true })
    try {
      const unassignedMembers = await membersService.getAllMembers({ department: "unassigned" })
      set({ unassignedMembers, isLoadingUnassigned: false })
    } catch (error: any) {
      set({ isLoadingUnassigned: false })
      notifications.show({
        title: "Error Loading Unassigned Members",
        message: error.message || "Failed to fetch unassigned members",
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
    }
  },

  /**
   * Get member by ID
   */
  getMemberById: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      const member = await membersService.getMemberById(id)
      set({ selectedMember: member, isLoading: false })
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch member details"
      set({ error: errorMessage, isLoading: false })
      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
      })
    }
  },

  /**
   * Create a new member
   */
  createMember: async (formData: MemberFormData) => {
    set({ isLoading: true, error: null })
    try {
      const newMember = await membersService.createMember(formData)

      // Show success notification
      notifications.show({
        title: "Member Created",
        message: `${newMember.first_name} ${newMember.last_name} has been successfully created`,
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      // Refetch members to get complete data with department_name and other populated fields
      await get().fetchMembers()

      return newMember
    } catch (error: any) {
      const errorMessage = error.message || "Failed to create member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Creation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Update an existing member
   */
  updateMember: async (id: number, formData: Partial<MemberFormData>) => {
    set({ isLoading: true, error: null })
    try {
      const updatedMember = await membersService.updateMember(id, formData)

      // Refetch members to get complete data with department_name and other populated fields
      await get().fetchMembers()

      // Update selected member with fresh data
      const freshMember = get().members.find((m) => m.id === id)
      set({ selectedMember: freshMember || updatedMember })

      return updatedMember
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Update Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Update only a member's first and last name (PATCH). A department admin may change
   * nothing else, and the backend rejects any other field.
   */
  updateMemberProfile: async (id: number, profile: MemberProfileFormData) => {
    set({ isLoading: true, error: null })
    try {
      const updatedMember = await membersService.updateMemberProfile(id, profile)

      // Merge the PATCH response instead of refetching the list: a failed refetch
      // would report an error right after a save that succeeded.
      const members = get().members.map((m) => (m.id === id ? { ...m, ...updatedMember } : m))
      set({
        members,
        selectedMember: members.find((m) => m.id === id) ?? updatedMember,
        isLoading: false,
      })

      return updatedMember
    } catch (error: unknown) {
      const errorMessage = (error instanceof Error && error.message) || "Failed to update member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Update Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Reactivate a deactivated member. Kept separate from updateMember so a
   * reactivation is never bundled with a profile edit in the same request.
   */
  activateMember: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      const activatedMember = await membersService.activateMember(id)

      await get().fetchMembers()

      const freshMember = get().members.find((m) => m.id === id)
      set({ selectedMember: freshMember || activatedMember, isLoading: false })

      notifications.show({
        title: "Member Reactivated",
        message: `${activatedMember.first_name} ${activatedMember.last_name} has been reactivated`,
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return activatedMember
    } catch (error: any) {
      const errorMessage = error.message || "Failed to reactivate member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Reactivation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Deactivate a member. Kept separate from updateMember (US-008) so
   * deactivation always goes through the dedicated endpoint — with its
   * session revocation, department/cost_limit clearing, and audit log —
   * rather than being bundled into a profile edit.
   */
  deactivateMember: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      const deactivatedMember = await membersService.deactivateMember(id)

      await get().fetchMembers()

      const freshMember = get().members.find((m) => m.id === id)
      set({ selectedMember: freshMember || deactivatedMember, isLoading: false })

      notifications.show({
        title: "Member Deactivated",
        message: `${deactivatedMember.first_name} ${deactivatedMember.last_name} has been deactivated`,
        color: "orange",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return deactivatedMember
    } catch (error: any) {
      const errorMessage = error.message || "Failed to deactivate member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Deactivation Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return null
    }
  },

  /**
   * Update permissions for an existing member (assign/remove diff)
   */
  updateMemberPermissions: async (id: number, permissionTypes: PermissionType[]) => {
    set({ isLoading: true, error: null })
    try {
      const currentMember = get().members.find((m) => m.id === id)
      const currentPerms: PermissionType[] = currentMember?.permission_types ?? []

      const toAdd = permissionTypes.filter((p) => !currentPerms.includes(p))
      const toRemove = currentPerms.filter((p) => !permissionTypes.includes(p))

      if (toAdd.length > 0) await membersService.assignPermissions(id, toAdd)
      if (toRemove.length > 0) await membersService.removePermissions(id, toRemove)

      await get().fetchMembers()

      const freshMember = get().members.find((m) => m.id === id)
      if (freshMember) {
        set({ selectedMember: freshMember })
      }

      notifications.show({
        title: "Permissions Updated",
        message: "Member permissions have been successfully updated",
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update permissions. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Permissions Update Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return false
    }
  },

  /**
   * Delete a member
   */
  deleteMember: async (id: number) => {
    set({ isLoading: true, error: null })
    try {
      await membersService.deleteMember(id)

      // Remove from members list
      set((state) => ({
        members: state.members.filter((m) => m.id !== id),
        selectedMember: null,
        isLoading: false,
      }))

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to delete member. Please try again."
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Deletion Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return false
    }
  },

  /**
   * Assign an existing (typically unassigned) member to a department
   */
  assignMemberToDepartment: async (memberId: number, departmentId: number) => {
    try {
      await membersService.assignMemberToDepartment(memberId, departmentId)
      await Promise.all([get().fetchMembers(), get().fetchUnassignedMembers()])

      notifications.show({
        title: "Member Assigned",
        message: "Member has been assigned to the department",
        color: "green",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to assign member. Please try again."
      notifications.show({
        title: "Assignment Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
      return false
    }
  },

  /**
   * Unassign a member from their current department (account itself is untouched)
   */
  removeMemberFromDepartment: async (memberId: number) => {
    try {
      await membersService.removeMemberFromDepartment(memberId)
      await Promise.all([get().fetchMembers(), get().fetchUnassignedMembers()])

      notifications.show({
        title: "Member Unassigned",
        message: "Member has been removed from the department",
        color: "orange",
        icon: createElement(CheckCircle, { size: 18 }),
        autoClose: 4000,
        withCloseButton: true,
      })

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to unassign member. Please try again."
      notifications.show({
        title: "Unassign Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })
      return false
    }
  },

  /**
   * Bulk action on multiple members
   */
  bulkAction: async () => {
    set({ isLoading: true, error: null })
    try {
      // Refresh members list after bulk action
      await get().fetchMembers()

      return true
    } catch (error: any) {
      const errorMessage = error.message || "Failed to perform bulk action"
      set({ error: errorMessage, isLoading: false })

      notifications.show({
        title: "Bulk Action Failed",
        message: errorMessage,
        color: "red",
        icon: createElement(AlertCircle, { size: 18 }),
        autoClose: 5000,
        withCloseButton: true,
      })

      return false
    }
  },

  /**
   * Set selected member
   */
  setSelectedMember: (member: Member | null) => {
    set({ selectedMember: member })
  },

  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null })
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState)
  },
}))
